/**
 * useStudentAssessmentForm — la feuille « Ajouter un contrôle » / « Modifier le contrôle » de
 * l'élève : son brouillon, sa photo et son analyse, ses chapitres, son envoi.
 *
 * Port d'`AddSchoolAssessmentModal.js` + `SchoolAssessmentTopicForm.js`, par les mêmes services :
 * - l'analyse d'une photo ou d'un PDF : `processSchoolAssessment` après l'envoi des fichiers
 *   (`useSchoolAssessmentAnalyzeAdapter`, le crochet du parent) ; ce qu'elle lit remplit le
 *   brouillon (matière, chapitres, « À réviser », et la date — que l'ancien code oubliait) ;
 * - les chapitres : `getChaptersBySchoolSubjectId` à chaque matière choisie, POUR LA CLASSE de
 *   l'élève (`{ class }`, comme l'ancien formulaire), filtrés sur la matière ;
 * - l'envoi : `createSchoolAssessment`, ou `updateSchoolAssessment` en modification, avec le corps
 *   de l'ancien écran ; le réducteur `schoolAssessments` range la réponse dans la liste ;
 * - le succès referme la feuille sans message (parité) ; un refus reste dans la feuille, au-dessus
 *   du bouton, avec la première erreur de champ de Laravel (`getErrorMessage`). L'ancien écran
 *   calculait ce message sans jamais l'afficher.
 *
 * Composition : trois crochets de `ui/hooks` (médias, analyse, écriture) et un réducteur pour ce qui
 * bouge ensemble (ouverte, quel contrôle, brouillon, erreur). Les médias gardent deux `useState` :
 * le sélecteur de médias demande leurs deux setters.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { formIds } from "../../../../../../data/constants/formConstants"
import { apiRequest } from "../../../../../../data/redux/actions/api"
import { chaptersService } from "../../../../../../data/services/chaptersService"
import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import { type StudentAssessmentFormProps } from "../../../../../../design-system/student"
import { type MediaPickerProps } from "../../../../../../design-system/organisms/MediaPicker"
import {
    mergeChapters,
    toStudentAssessmentPayload,
    type StudentAssessmentEdit,
} from "../../../../adapters/student/Assessments/studentAssessmentsAdapter"
import { studentAssessmentChaptersViewModel } from "../../../../view-models/student/Assessments/studentAssessmentsViewModel"
import {
    applySchoolAssessmentPreview,
    canSubmitCreateSchoolAssessment,
    emptyCreateSchoolAssessment,
} from "../../../formatters/createSchoolAssessmentFormatter"
import { studentRefusal, studentRefusalText } from "../../../hooks/studentRefusal"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useMediaPickerAdapter } from "../../../hooks/useMediaPickerAdapter"
import { useSchoolAssessmentAnalyzeAdapter } from "../../../hooks/useSchoolAssessmentAnalyzeAdapter"
import { type UICreateSchoolAssessment, type UISchoolAssessment, type UISchoolAssessmentPreview } from "../../../types"

type PickedImage = { uri: string; width?: number; height?: number }
type AssessmentId = UISchoolAssessment["id"]

/** Le repli de l'ancien `getErrorMessage`. */
const ASSESSMENT_SAVE_FALLBACK = "Une erreur est survenue"
/** Le repli de l'analyse (`useSchoolAssessmentAnalyzeAdapter`), quand le serveur vouvoie. */
const ASSESSMENT_ANALYZE_FALLBACK = "Une erreur est survenue pendant l'analyse."

/**
 * Le message d'un refus d'enregistrement — port de `getErrorMessage` (`AddSchoolAssessmentModal.js`) :
 * la première erreur du premier champ refusé par Laravel, sinon le message, sinon le repli.
 */
export function assessmentSaveErrorMessage(error: unknown): string {
    const fields = (error as { errors?: unknown } | null | undefined)?.errors
    if (fields != null && typeof fields === "object") {
        const first = Object.values(fields as Record<string, unknown>)[0]
        if (Array.isArray(first) && typeof first[0] === "string" && first[0] !== "")
            return studentRefusalText(first[0], ASSESSMENT_SAVE_FALLBACK)
    }
    return studentRefusal(error, ASSESSMENT_SAVE_FALLBACK)
}

export type StudentAssessmentFormUi = {
    visible: boolean
    /** Le contrôle qu'on modifie — `null` : un ajout. */
    editingId: AssessmentId | null
    /** Les chapitres du contrôle qu'on modifie : la liste sait les nommer même hors catalogue. */
    editChapters: StudentAssessmentEdit["chapters"]
    draft: UICreateSchoolAssessment
    error: string | null
}

export type StudentAssessmentFormAction =
    | { type: "OPEN_ADD"; draft: UICreateSchoolAssessment }
    | { type: "OPEN_EDIT"; id: AssessmentId; edit: StudentAssessmentEdit }
    | { type: "CLOSE" }
    | { type: "CHANGE"; draft: UICreateSchoolAssessment }
    | { type: "PREVIEW"; preview: UISchoolAssessmentPreview }
    | { type: "FAIL"; error: string }
    | { type: "CLEAR_ERROR" }

const initialStudentAssessmentFormUi = (): StudentAssessmentFormUi => ({
    visible: false,
    editingId: null,
    editChapters: [],
    draft: emptyCreateSchoolAssessment(),
    error: null,
})

function studentAssessmentFormReducer(
    state: StudentAssessmentFormUi,
    action: StudentAssessmentFormAction
): StudentAssessmentFormUi {
    switch (action.type) {
        case "OPEN_ADD":
            return { visible: true, editingId: null, editChapters: [], draft: action.draft, error: null }
        case "OPEN_EDIT":
            return {
                visible: true,
                editingId: action.id,
                editChapters: action.edit.chapters,
                draft: action.edit.draft,
                error: null,
            }
        case "CLOSE":
            // Le brouillon reste jusqu'à la prochaine ouverture : la feuille se referme sans se vider à l'écran.
            return { ...state, visible: false, error: null }
        case "CHANGE":
            return { ...state, draft: action.draft }
        case "PREVIEW":
            return { ...state, draft: applySchoolAssessmentPreview(state.draft, action.preview), error: null }
        case "FAIL":
            return { ...state, error: action.error }
        case "CLEAR_ERROR":
            return state.error == null ? state : { ...state, error: null }
        default:
            return state
    }
}

export type UseStudentAssessmentFormArgs = {
    studentId: string | number | null
    studentClass: string | null
    subjects: StudentAssessmentFormProps["subjects"]
    edits: Record<string, StudentAssessmentEdit>
}

export function useStudentAssessmentForm(args: UseStudentAssessmentFormArgs) {
    const { studentId, studentClass, subjects, edits } = args
    const dispatch = useDispatch()
    const [ui, dispatchUi] = useReducer(studentAssessmentFormReducer, undefined, initialStudentAssessmentFormUi)
    const [images, setImages] = useState<PickedImage[]>([])
    const [pdf, setPdf] = useState<string | null>(null)
    const isEditing = ui.editingId != null

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cameraOn = useSelector((state: any) => !!state?.activity?.cameraOn)
    const { chapters: catalog, chaptersLoading } = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => studentAssessmentChaptersViewModel(state, ui.draft.subjectId)
    )

    // Les chapitres de la matière, relus à chaque matière choisie tant que la feuille est ouverte.
    const lastChaptersSubjectRef = useRef<string | null>(null)
    useEffect(() => {
        const subjectId = ui.draft.subjectId
        if (!ui.visible || subjectId == null || lastChaptersSubjectRef.current === String(subjectId)) return
        lastChaptersSubjectRef.current = String(subjectId)
        dispatch(
            apiRequest(
                chaptersService.getChaptersBySchoolSubjectId,
                { schoolSubjectId: subjectId },
                {},
                { class: studentClass ?? undefined }
            ) as never
        )
    }, [dispatch, ui.visible, ui.draft.subjectId, studentClass])

    const resetMedia = useCallback(() => {
        setImages([])
        setPdf(null)
        lastChaptersSubjectRef.current = null
    }, [])

    const openAdd = useCallback(() => {
        resetMedia()
        dispatchUi({ type: "OPEN_ADD", draft: emptyCreateSchoolAssessment() })
    }, [resetMedia])

    const openEdit = useCallback(
        (id: AssessmentId) => {
            const edit = edits[String(id)]
            if (edit == null) return
            resetMedia()
            dispatchUi({ type: "OPEN_EDIT", id, edit })
        },
        [edits, resetMedia]
    )

    const close = useCallback(() => dispatchUi({ type: "CLOSE" }), [])

    const analyze = useSchoolAssessmentAnalyzeAdapter({
        studentId,
        images,
        pdf,
        onSuccess: (preview) => dispatchUi({ type: "PREVIEW", preview }),
        onError: (message) =>
            dispatchUi({ type: "FAIL", error: studentRefusalText(message, ASSESSMENT_ANALYZE_FALLBACK) }),
    })

    const startAnalyze = analyze.onAnalyze
    const onAnalyze = useCallback(() => {
        dispatchUi({ type: "CLEAR_ERROR" })
        startAnalyze()
    }, [startAnalyze])

    const mediaProps = useMediaPickerAdapter({
        images,
        setImages,
        pdf,
        setPdf,
        multiple: true,
        enableExternalLink: false,
        buttonLabel: "Photo ou document",
        formId: formIds.NEW_SCHOOL_ASSESSMENT,
        // L'ancien bouton cachait « Ajouter » une fois l'analyse lancée ou lue.
        showAddMoreButton: ui.draft.aiPreview == null && !analyze.analyzeLoading,
    }) as MediaPickerProps

    /** Un seul envoi : un contrôle à modifier choisit le service. */
    const { start, requesting } = useApiMutation({
        service: isEditing
            ? schoolAssessmentsService.updateSchoolAssessment
            : schoolAssessmentsService.createSchoolAssessment,
        voice: "student",
        idleOnUnmount: [
            schoolAssessmentsService.createSchoolAssessment,
            schoolAssessmentsService.updateSchoolAssessment,
        ],
        onSuccess: () => {
            resetMedia()
            dispatchUi({ type: "CLOSE" })
        },
        onError: (_message, error) => dispatchUi({ type: "FAIL", error: assessmentSaveErrorMessage(error) }),
    })

    const onSubmit = useCallback(() => {
        if (studentId == null || requesting || !canSubmitCreateSchoolAssessment(ui.draft)) return
        dispatchUi({ type: "CLEAR_ERROR" })
        start(ui.editingId != null ? { id: ui.editingId } : {}, toStudentAssessmentPayload(studentId, ui.draft))
    }, [start, requesting, studentId, ui.draft, ui.editingId])

    const chapters = useMemo(
        () => mergeChapters(catalog, ui.draft.aiPreview?.chapters ?? [], ui.editChapters),
        [catalog, ui.draft.aiPreview?.chapters, ui.editChapters]
    )

    const assessmentForm: StudentAssessmentFormProps = {
        visible: ui.visible,
        onClose: close,
        createAssessmentData: ui.draft,
        onCreateAssessmentDataChange: (draft) => dispatchUi({ type: "CHANGE", draft }),
        subjects,
        chapters,
        chaptersLoading,
        mediaProps,
        hidden: cameraOn,
        onAnalyze,
        analyzeLoading: analyze.analyzeLoading,
        onSubmit,
        submitLoading: requesting,
        errorMessage: ui.error,
        isEditing,
    }

    return { assessmentForm, openAdd, openEdit }
}
